import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toaster';
import { useHasRole } from '@/features/auth/AuthProvider';
import { addUnit, fetchUnits } from './setupQueries';

export function UnitFormDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated?: (unit: { id: string; name: string }) => void;
}) {
  const qc = useQueryClient();
  const canWrite = useHasRole('owner');
  const [name, setName] = useState('');

  useEffect(() => {
    if (!open) return;
    setName('');
  }, [open]);

  const save = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Unit name is required');

      const existing = await fetchUnits();
      if (existing.some((u) => u.name.toLowerCase() === name.trim().toLowerCase())) {
        throw new Error('This unit already exists');
      }

      const id = await addUnit(name.trim());
      return { id, name: name.trim() };
    },
    onSuccess: (unit) => {
      qc.invalidateQueries({ queryKey: ['inventory-units'] });
      toast({ title: 'Unit added', variant: 'success' });
      onCreated?.(unit);
      onOpenChange(false);
    },
    onError: (e) => toast({ title: 'Failed', description: String(e), variant: 'destructive' }),
  });

  if (!canWrite) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add unit</DialogTitle>
          <DialogDescription>
            Units of measure appear when creating or editing products.
          </DialogDescription>
        </DialogHeader>

        <div>
          <Label>Unit name *</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. piece, box, sqft"
            className="mt-1.5"
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="gold" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
